/**
 * DashboardGroupsSection - Displays dashboards in the sidebar grouped by preference
 *
 * When groupBy is "none" (the default), renders GenericResourceGroup directly
 * to provide folder support (create, display, drag-into). For other groupBy
 * modes, delegates to SidebarResourceSection for search/filter/grouping.
 */

import React, { useCallback, useMemo } from "react";
import { Dashboard } from "@/models/dashboard";
import { useDashboards, useUpdateDashboard } from "@/api/dashboard";
import { useResourceGroups } from "@/api/resource-group";
import { useDashboardGroupBy } from "@/hooks/use-resource-group-by";
import { useSidebarContext } from "@/hooks/use-sidebar";
import { dashboardGalleryConfig } from "@/config/resource-gallery-configs";
import { dashboardSectionConfig } from "@/config/sidebar-section-configs";
import SidebarResourceSection, {
  SidebarGroupWithItemsRenderProps,
} from "./sidebar-resource-section";
import GenericResourceGroup from "./generic-resource-group";
import DashboardItem from "./dashboard-item";
import { GroupableItem } from "./resource-group-item";

// =============================================================================
// Types
// =============================================================================

interface DashboardGroupsSectionProps {
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

type DashboardGroupable = Dashboard & GroupableItem;

// =============================================================================
// Component
// =============================================================================

const DashboardGroupsSection: React.FC<DashboardGroupsSectionProps> = ({
  onMultiSelectContextMenu,
}) => {
  const { data: allDashboards = [] } = useDashboards();
  const { data: resourceGroups = [] } = useResourceGroups("dashboard");
  const { groupBy } = useDashboardGroupBy();
  const { getPinnedDashboards } = useSidebarContext();
  const updateDashboardMutation = useUpdateDashboard();

  // Exclude pinned dashboards (they appear in PinnedSection above)
  const dashboards = useMemo(() => {
    const pinnedIds = new Set(
      getPinnedDashboards(allDashboards).map((d) => d.id)
    );
    return allDashboards.filter((d) => !pinnedIds.has(d.id));
  }, [allDashboards, getPinnedDashboards]);

  const orderedIds = useMemo(
    () => dashboards.map((d) => d.id!),
    [dashboards]
  );

  const updateItemGroup = useCallback(
    async (itemId: string, groupId: string) => {
      await updateDashboardMutation.mutateAsync({
        group: { id: groupId },
        uriParams: { id: itemId },
      });
    },
    [updateDashboardMutation.mutateAsync]
  );

  // 5-arg render used by GenericResourceGroup (includes onDropToCreateGroup)
  const renderDashboardItem = useCallback(
    (
      dashboard: Dashboard,
      isItemSelected: boolean,
      itemOrderedIds: string[],
      multiSelectContextMenu?: (e: React.MouseEvent) => void,
      onDropToCreateGroup?: (draggedId: string, targetId: string) => void
    ) => (
      <DashboardItem
        key={dashboard.id}
        dashboard={dashboard}
        isSelected={isItemSelected}
        orderedIds={itemOrderedIds}
        onMultiSelectContextMenu={multiSelectContextMenu}
        onDropToCreateGroup={onDropToCreateGroup}
      />
    ),
    []
  );

  // Render a group using GenericResourceGroup (used by SidebarResourceSection for grouped modes)
  const renderGroup = useCallback(
    (props: SidebarGroupWithItemsRenderProps<Dashboard>) => (
      <GenericResourceGroup<DashboardGroupable>
        key={props.groupKey}
        groupKey={props.groupKey}
        items={props.items as DashboardGroupable[]}
        groupBy={props.groupBy}
        resourceType="dashboard"
        renderItem={renderDashboardItem}
        getGroupIcon={dashboardSectionConfig.getGroupIcon}
        resourceGroups={resourceGroups}
        allItems={dashboards as DashboardGroupable[]}
        updateItemGroup={updateItemGroup}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [renderDashboardItem, resourceGroups, dashboards, updateItemGroup]
  );

  // 4-arg object render used by SidebarResourceSection flat path
  const renderItem = useCallback(
    (props: {
      item: Dashboard;
      isSelected: boolean;
      orderedIds: string[];
      onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
    }) => (
      <DashboardItem
        key={props.item.id}
        dashboard={props.item}
        isSelected={props.isSelected}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    []
  );

  // Default mode: render GenericResourceGroup directly for full folder support
  if (groupBy === "none") {
    if (dashboards.length === 0) {
      return null;
    }

    return (
      <div className="px-3 py-2">
        <GenericResourceGroup<DashboardGroupable>
          groupKey="all"
          items={dashboards as DashboardGroupable[]}
          groupBy="none"
          resourceType="dashboard"
          renderItem={renderDashboardItem}
          getGroupIcon={dashboardSectionConfig.getGroupIcon}
          resourceGroups={resourceGroups}
          allItems={dashboards as DashboardGroupable[]}
          updateItemGroup={updateItemGroup}
          orderedIds={orderedIds}
          onMultiSelectContextMenu={onMultiSelectContextMenu}
        />
      </div>
    );
  }

  // Grouped modes: use SidebarResourceSection for search/filter/grouping
  return (
    <SidebarResourceSection<Dashboard>
      items={dashboards}
      config={dashboardGalleryConfig}
      renderItem={renderItem}
      renderGroup={renderGroup}
      emptyMessage={dashboardSectionConfig.emptyMessage}
      noResultsMessage={dashboardSectionConfig.noResultsMessage}
      searchPlaceholder={dashboardSectionConfig.searchPlaceholder}
      onMultiSelectContextMenu={onMultiSelectContextMenu}
      externalGroupBy={groupBy}
    />
  );
};

DashboardGroupsSection.displayName = "DashboardGroupsSection";

export default DashboardGroupsSection;
